export const COUNTRY_CA = 0;
export const COUNTRY_US = 1;

export const PAYMENT_PURPOSE_REVENUE = 'revenue' as const;
export const PAYMENT_PURPOSE_NATIVE = 'native' as const;

export type PaymentPurpose = typeof PAYMENT_PURPOSE_REVENUE | typeof PAYMENT_PURPOSE_NATIVE | string;

type OfficePaymentLocation = {
  country?: number | string | null;
  stripeCustomerId?: string | null;
  revenueStripeCustomerId?: string | null;
  isCardValid?: number | string | boolean | null;
  revenuePaymentInfoValid?: number | string | boolean | null;
  hasCard?: boolean | null;
};

export const isValidPaymentFlag = (value: unknown) => value === true || value === 1 || value === '1';

export const withFallbackPaymentCountry = <T extends OfficePaymentLocation>(
  location?: T | null,
  fallbackCountry?: number | string | null,
) => {
  if (!location) return location;
  if (location.country !== undefined && location.country !== null && location.country !== '') return location;
  if (fallbackCountry === undefined || fallbackCountry === null || fallbackCountry === '') return location;
  return { ...location, country: fallbackCountry };
};

export const isUSLocation = (location?: OfficePaymentLocation | null) => (
  Number(location?.country) === COUNTRY_US || String(location?.country).toUpperCase() === 'US'
);

const getNumericCountry = (country?: number | string | null) => {
  if (country === undefined || country === null) return undefined;
  const countryString = String(country).toUpperCase();
  if (countryString === 'US') return COUNTRY_US;
  if (countryString === 'CA') return COUNTRY_CA;
  return Number(country);
};

export const isRevenuePurpose = (paymentPurpose?: PaymentPurpose) => (
  !paymentPurpose || paymentPurpose === PAYMENT_PURPOSE_REVENUE
);

export const getRevenueStripeCountry = (location?: OfficePaymentLocation | null) => (
  isUSLocation(location) ? COUNTRY_CA : getNumericCountry(location?.country)
);

export const getRevenueCustomerId = (location?: OfficePaymentLocation | null) => (
  isUSLocation(location) ? location?.revenueStripeCustomerId : location?.stripeCustomerId
);

export const getPaymentStripeCountry = (
  location?: OfficePaymentLocation | null,
  paymentPurpose?: PaymentPurpose,
) => (
  isRevenuePurpose(paymentPurpose) ? getRevenueStripeCountry(location) : getNumericCountry(location?.country)
);

export const getPaymentCustomerId = (
  location?: OfficePaymentLocation | null,
  paymentPurpose?: PaymentPurpose,
) => (
  isRevenuePurpose(paymentPurpose) ? getRevenueCustomerId(location) : location?.stripeCustomerId
);

export const getRevenuePaymentInfoStatus = (location?: OfficePaymentLocation | null) => (
  isUSLocation(location) ? location?.revenuePaymentInfoValid : location?.isCardValid
);

export const hasOfficeRevenuePaymentInfo = (location?: OfficePaymentLocation | null) => (
  Boolean(getRevenueCustomerId(location))
  && isValidPaymentFlag(location?.isCardValid)
  && (!isUSLocation(location) || isValidPaymentFlag(location?.revenuePaymentInfoValid))
);

export const toRevenueBillingLocation = <T extends OfficePaymentLocation>(location: T): T & { hasCard: boolean } => ({
  ...location,
  hasCard: hasOfficeRevenuePaymentInfo(location),
});
